import { Button } from "@/components/ui/button";
import { ArrowRight, Star } from "lucide-react";
import { useState } from "react";
import heroVideo from "@/assets/playfield-teaser-720.mp4";
import heroVideoMobile from "@/assets/playfield-teaser-640.mp4";
import heroPoster from "@/assets/hero-poster.jpg";
import { useLanguage } from "@/contexts/LanguageContext";
import { translations } from "@/i18n/translations";
import { trackEvent, AnalyticsEvents } from "@/utils/analytics";

const Hero = () => {
  const { language } = useLanguage();
  const t = translations[language].hero;
  const [videoSrc] = useState(() =>
    typeof window !== "undefined" && window.matchMedia("(max-width: 768px)").matches
      ? heroVideoMobile
      : heroVideo
  );

  const scrollTo = (id: string) => {
    const element = document.getElementById(id);
    if (element) element.scrollIntoView({ behavior: "smooth" });
  };


  return (
    <section
      id="home"
      className="relative min-h-[85vh] sm:min-h-[92vh] flex items-center justify-center overflow-hidden"
    >
      {/* Background video with clean overlay */}
      <div className="absolute inset-0 z-0">
        <video
          src={videoSrc}
          poster={heroPoster}
          autoPlay
          muted
          loop
          playsInline
          preload="auto"
          aria-label="VR teambuilding ervaring met professionele VR apparatuur - PlayfieldVR bedrijfsuitje en groepsactiviteit"
          className="w-full h-full object-cover opacity-50"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-background/45 via-background/60 to-background"></div>
      </div>

      {/* Content */}
      <div className="container mx-auto px-6 lg:px-8 z-10 text-center pt-28 pb-16 sm:pt-36 sm:pb-32">
        <div className="max-w-3xl mx-auto">
          <p className="text-xs sm:text-sm uppercase tracking-[0.2em] text-muted-foreground mb-6 sm:mb-10">
            {t.teamBuildingBadge}
          </p>

          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight leading-[1.05] mb-5 sm:mb-8">
            <span className="block">{t.title}</span>
            <span className="block text-primary mt-1">{t.subtitle}</span>
          </h1>

          <p className="text-base sm:text-lg text-muted-foreground max-w-xl mx-auto mb-10 sm:mb-14">
            {t.description}
          </p>

          <div className="mb-6 sm:mb-8 flex items-center justify-center gap-2 text-xs sm:text-sm text-muted-foreground">
            <span className="flex items-center gap-0.5 text-primary" aria-hidden="true">
              {[0, 1, 2, 3, 4].map((i) => (
                <Star key={i} className="w-3.5 h-3.5 fill-current" />
              ))}
            </span>
            <span>{t.socialProof}</span>
          </div>

          <div className="mb-4 sm:mb-5">
            <p className="text-base sm:text-lg font-bold text-primary">
              {t.priceAnchor}
            </p>
          </div>

          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
            <Button
              size="lg"
              onClick={() => {
                trackEvent(AnalyticsEvents.CTA_CLICK, { button: 'book_experience', location: 'hero' });
                scrollTo("contact-form");
              }}
              className="bg-primary text-primary-foreground font-semibold text-base px-10 py-6 hover:bg-primary/90 transition-colors group w-full sm:w-auto"
            >
              {t.bookExperience}
              <ArrowRight className="ml-2 w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
            </Button>
            <Button
              size="lg"
              variant="ghost"
              onClick={() => {
                trackEvent(AnalyticsEvents.CTA_CLICK, { button: 'micro_cta', location: 'hero' });
                scrollTo("audience");
              }}
              className="text-muted-foreground hover:text-foreground hover:bg-transparent border border-border font-normal text-base px-10 py-6 transition-colors w-full sm:w-auto"
            >
              {t.microCta}
            </Button>
          </div>

          <p className="mt-8 sm:mt-10 text-xs sm:text-sm text-muted-foreground/80 tracking-wide">
            {t.reassurance}
          </p>
        </div>
      </div>
    </section>
  );
};

export default Hero;
